/**
 * Sanitize a Claude Code .mcp.json file for compatibility.
 *
 * Fixes:
 * 1. Relative proxy URLs (e.g., /mcp-proxy/...) — resolved to absolute if
 *    apiHost is provided, otherwise removed.
 * 2. URL-based entries (type: "sse") — converted to mcp-remote stdio bridge
 *    since Claude Code doesn't support SSE MCP servers natively.
 *
 * Returns true if the file was modified.
 */

import { readFileSync, writeFileSync } from 'node:fs';

export function sanitizeMcpJson(
  mcpConfigPath: string,
  apiHost?: string,
): boolean {
  try {
    const mcpRaw = JSON.parse(readFileSync(mcpConfigPath, 'utf-8'));
    const servers = mcpRaw.mcpServers as Record<string, Record<string, unknown>> | undefined;
    if (!servers) return false;

    let changed = false;
    for (const [key, val] of Object.entries(servers)) {
      if (typeof val?.url !== 'string') continue;

      // Resolve relative URLs
      if (val.url.startsWith('/')) {
        if (apiHost) {
          val.url = `${apiHost}${val.url}`;
          changed = true;
        } else {
          delete servers[key];
          changed = true;
          continue;
        }
      }

      // ENG-5071: do NOT wrap URL-based entries that carry auth headers in
      // mcp-remote. mcp-remote can't pass headers through to the upstream
      // MCP server, so the conversion silently drops the Authorization
      // header and the call fails at runtime. claudecode/index.ts's
      // writeMcpServer explicitly emits the raw `{ url, headers }` shape
      // for this case (ENG-4694).
      //
      // ENG-5074: Claude Code's MCP schema additionally requires a `type`
      // field on URL-based entries — without it claude rejects the
      // config at startup ("Does not adhere to MCP server configuration
      // schema") and the tmux session exits inside a second, putting
      // the agent in a respawn loop. Existing on-disk files written by
      // the pre-ENG-5074 writer carry url+headers but no type — backfill
      // 'http' (Streamable HTTP, the default for OAuth-MCP integrations)
      // so the sanitizer self-heals these entries instead of leaving
      // them to fail in claude. New writes from the post-ENG-5074
      // writer already include the field, so this is a no-op for them.
      const headers = val.headers as Record<string, unknown> | undefined;
      if (headers && typeof headers === 'object' && Object.keys(headers).length > 0) {
        if (typeof val.type !== 'string') {
          val.type = 'http';
          changed = true;
        }
        continue;
      }

      // Convert URL-based entries to mcp-remote stdio bridge
      // Claude Code doesn't support type: "sse" natively
      const url = val.url as string;
      delete val.url;
      delete val.type;
      val.command = 'npx';
      val.args = ['-y', 'mcp-remote', url, '--allow-http'];
      changed = true;
    }

    if (changed) writeFileSync(mcpConfigPath, JSON.stringify(mcpRaw, null, 2));
    return changed;
  } catch {
    return false;
  }
}
